/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

window.customElements.define('cart-drawer', CartDrawer);

class CartQuickProduct extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-quick-product__input');
    if (!this.input || this.boundChange) return;

    this.boundChange = this.handleChange.bind(this);
    this.input.addEventListener('change', this.boundChange);
  }

  disconnectedCallback() {
    if (this.input && this.boundChange) {
      this.input.removeEventListener('change', this.boundChange);
    }
  }

  handleChange() {
    if (this.busy) {
      this.input.checked = !this.input.checked;
      return;
    }

    if (this.input.checked) {
      this.addProduct();
    } else {
      this.removeProduct();
    }
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.input.disabled = isBusy;
    this.classList.toggle('is-loading', isBusy);
  }

  notifyCartChange() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  }

  async addProduct() {
    const variantId = this.dataset.variantId;
    if (!variantId) {
      this.input.checked = false;
      return;
    }

    this.setBusy(true);

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1
        })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || response.status);
      }
      this.notifyCartChange();
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
      this.input.checked = false;
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: error.message || String(error) }
      }));
    } finally {
      this.setBusy(false);
    }
  }

  async removeProduct() {
    const keys = (this.dataset.lineKeys || '').split(',').map((key) => key.trim()).filter(Boolean);
    if (!keys.length) {
      this.input.checked = false;
      return;
    }

    this.setBusy(true);

    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    try {
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      this.notifyCartChange();
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
      this.input.checked = true;
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: error.message || String(error) }
      }));
    } finally {
      this.setBusy(false);
    }
  }
}

if (!customElements.get('cart-quick-product')) {
  customElements.define('cart-quick-product', CartQuickProduct);
}
